import React, {
    createContext,
    useContext,
    useReducer,
    ReactNode,
    useState,
    useCallback,
    useEffect,
    useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get2FATokenForLogin, login, loginFailed, toggleAuthModal } from '../redux/authentication/action';
import { TUsernamePassword } from '../components/EsLoginForm/EsSigninData';
import {
    getPhoneVerificationCode,
    createUserProfile,
    TUserField,
    IFieldAvailabilityData,
    startUserDetailsAvailabilitySearch,
    updateUserProfile,
} from '../redux/user/actions';
import { SignUpdata } from '../components/EsSignupForm/EsSignupForm';
import { getJWtDetails, useDispatchPromise } from '../utils/AppUtils';
import { authModalSelector, userActionSelector } from '../selectors/selectors';
import ReCAPTCHA from 'react-google-recaptcha';
import { APP_ROUTES } from '../types/global/routes.types';
import { TOnChangePayload } from '../components/EsSignupForm/Interests';
import { SentryCapture } from '../analytics/Sentry';

// Base authentication state
export type LoggedInState = {
    tokenExists: boolean;
    user_id: string;
    isSignedIn: boolean;
    token?: string;
    setLoginStatus?: (token: string, isSignedIn: boolean, tokenExists: boolean, user_id: string) => void;
};

// Enhanced context with modal state and functions
export interface AuthModalState {
    isOpen: boolean;
    mode: 'login' | 'signup';
    step: number;
}

// Modal actions
type AuthModalAction =
    | { type: 'OPEN_MODAL'; payload: { mode: 'login' | 'signup' } }
    | { type: 'CLOSE_MODAL' }
    | { type: 'SET_MODE'; payload: { mode: 'login' | 'signup' } }
    | { type: 'NEXT_STEP' }
    | { type: 'PREV_STEP' }
    | { type: 'RESET_STEPS' };

// Initial modal state
const initialModalState: AuthModalState = {
    isOpen: false,
    mode: 'login',
    step: 0,
};

// Modal reducer
const modalReducer = (state: AuthModalState, action: AuthModalAction): AuthModalState => {
    switch (action.type) {
        case 'OPEN_MODAL':
            return {
                ...state,
                isOpen: true,
                mode: action.payload.mode,
                step: 0,
            };
        case 'CLOSE_MODAL':
            return {
                ...state,
                isOpen: false,
            };
        case 'SET_MODE':
            return {
                ...state,
                mode: action.payload.mode,
                step: 0,
            };
        case 'NEXT_STEP':
            return {
                ...state,
                step: state.step + 1,
            };
        case 'PREV_STEP':
            return {
                ...state,
                step: Math.max(0, state.step - 1),
            };
        case 'RESET_STEPS':
            return {
                ...state,
                step: 0,
            };
        default:
            return state;
    }
};

// Get authentication state
export const authenticationModule = (): LoggedInState => {
    const { isSignedIn, token, tokenExists, user_id } = getJWtDetails();
    return { tokenExists, user_id, isSignedIn, token };
};

// Enhanced auth context type
export interface AuthContextType extends LoggedInState {
    // Modal state and functions
    modalState: AuthModalState;
    openAuthModal: (mode?: 'login' | 'signup') => void;
    closeAuthModal: () => void;
    nextStep: () => void;
    prevStep: () => void;
    handleCheckUserAvailability: (data: SignUpdata, next: () => void, field: TUserField) => void;
    handleUsernameCheck: (userName: string) => void;

    // Auth actions
    handleLogin: (data: TUsernamePassword, callback?: () => void, isLastStep?: boolean) => Promise<any>;
    handleSignup: (data: SignUpdata, code?: string[], callback?: () => void) => Promise<any>;
    handleGetOTP: (data: SignUpdata, callback: () => void, resend?: boolean) => Promise<any>;
    handleResendLoginOtp: (data: TUsernamePassword) => Promise<any>;

    // Loading states
    isAuthLoading: boolean;
    isOtpLoading: boolean;
    isResendingOtp: boolean;
    loginMessage?: string;
    loginChannel?: 'sms' | 'whatsapp' | 'audme' | 'email';
    audmeData?: {
        token?: string;
        qrCode?: string;
        deeplink?: string;
    } | null;

    // Update User Details
    updateUserDetails: (details: TOnChangePayload, callBack?: () => void) => void;
}

// Create the enhanced context
export const AuthContext = createContext<AuthContextType>({
    // Base auth state
    tokenExists: false,
    user_id: '',
    isSignedIn: false,
    token: '',
    setLoginStatus: () => {},

    // Modal state and functions
    modalState: initialModalState,
    openAuthModal: () => {},
    closeAuthModal: () => {},
    nextStep: () => {},
    prevStep: () => {},
    handleCheckUserAvailability: () => {},
    handleUsernameCheck: () => {},

    // Auth actions
    handleLogin: async () => {},
    handleSignup: async () => {},
    handleGetOTP: async () => {},
    handleResendLoginOtp: async () => {},

    // Loading states
    isAuthLoading: false,
    isOtpLoading: false,
    isResendingOtp: false,
    loginMessage: undefined,
    loginChannel: undefined,
    audmeData: null,

    // User details
    updateUserDetails: () => {},
});

// Enhanced provider
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Get base auth state
    const baseAuth = authenticationModule();
    const { usernameTaken } = useSelector(userActionSelector);
    const showAuthModal = useSelector(authModalSelector);
    const { pathname } = window.location;

    // Modal state management
    const [modalState, dispatch] = useReducer(modalReducer, initialModalState);

    // Loading states
    const [isAuthLoading, setIsAuthLoading] = useState(false);
    const [isOtpLoading, setIsOtpLoading] = useState(false);
    const [isResendingOtp, setIsResendingOtp] = useState(false);
    const [loginMessage, setLoginMessage] = useState<string | undefined>(undefined);
    const [loginChannel, setLoginChannel] = useState<'sms' | 'whatsapp' | 'audme' | 'email' | undefined>(undefined);
    const [audmeData, setAudmeData] = useState<{
        token?: string;
        qrCode?: string;
        deeplink?: string;
    } | null>(null);

    const ref = useRef<ReCAPTCHA>();

    // For Redux interactions
    const reduxDispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();

    // Update login status
    const setLoginStatus = useCallback((token: string, isSignedIn: boolean, tokenExists: boolean, user_id: string) => {
        // This function is implemented by the existing authentication system
    }, []);

    // Modal control functions
    const openAuthModal = useCallback(
        (mode: 'login' | 'signup' = 'login') => {
            dispatch({ type: 'OPEN_MODAL', payload: { mode } });
            reduxDispatch(toggleAuthModal(true));
            setLoginMessage(undefined);
            setLoginChannel(undefined);
            setAudmeData(null);
        },
        [reduxDispatch],
    );

    const closeAuthModal = useCallback(() => {
        dispatch({ type: 'CLOSE_MODAL' });
        reduxDispatch(toggleAuthModal(false));
        setLoginMessage(undefined);
        setLoginChannel(undefined);
        setAudmeData(null);
    }, [reduxDispatch]);

    const nextStep = useCallback(() => {
        dispatch({ type: 'NEXT_STEP' });
    }, []);

    const prevStep = useCallback(() => {
        dispatch({ type: 'PREV_STEP' });
    }, []);

    useEffect(() => {
        /** Dismiss the modal if we are already on Signin / Login Page */
        if (pathname === APP_ROUTES.LOGIN_IN || pathname === APP_ROUTES.ESRESETPASSWORD) {
            closeAuthModal();
        }
    }, [pathname, closeAuthModal]);

    // Auth handling functions
    const handleLogin = useCallback(
        async (data: TUsernamePassword, callback?: () => void, isLastStep?: boolean) => {
            if (isLastStep) {
                setIsAuthLoading(true);
                setIsOtpLoading(true);
                try {
                    // Add quick_login_token if audme is selected and we have the token
                    const loginPayload = { ...data };
                    if (loginChannel === 'audme' && audmeData?.token) {
                        (loginPayload as any).quick_login_token = audmeData.token;
                    }
                    const result = await dispatchPromise(login(loginPayload));
                    setIsAuthLoading(false);
                    setIsOtpLoading(false);
                    closeAuthModal();

                    // Update auth state with new JWT details
                    const { isSignedIn, token, tokenExists, user_id } = getJWtDetails();
                    setLoginStatus(token || '', isSignedIn, tokenExists, user_id);

                    return result;
                } catch (error) {
                    setIsAuthLoading(false);
                    setIsOtpLoading(false);
                    reduxDispatch(
                        loginFailed({
                            data: {
                                message: error instanceof Error ? error.message : 'Unknown error',
                                status: 'error',
                            },
                            statusText: 'Error',
                            status: 500,
                        }),
                    );
                    SentryCapture(error);
                }
            } else {
                setIsAuthLoading(true);
                setIsOtpLoading(true);
                try {
                    ref.current?.reset();
                    const token = await ref.current?.executeAsync();
                    data.token = token as unknown as string;
                    const result: any = await dispatchPromise(get2FATokenForLogin(data));
                    setIsAuthLoading(false);
                    setIsOtpLoading(false);
                    const message = typeof result === 'string' ? result : result?.message;
                    const channel = result?.channel;

                    const { token: quickLoginToken, qrCode, deeplink } = result.audme || {};

                    // Store audme data if present in response
                    if (channel === 'audme' && quickLoginToken && qrCode && deeplink) {
                        setAudmeData({
                            token: quickLoginToken,
                            qrCode: qrCode,
                            deeplink: deeplink,
                        });
                    }

                    if (message && message === 'done') {
                        closeAuthModal();
                    } else if (callback) {
                        setLoginMessage(message);
                        setLoginChannel(channel);
                        callback();
                    }
                    // if (callback) callback();
                    return result;
                } catch (error) {
                    setIsAuthLoading(false);
                    SentryCapture(error);
                }
            }
        },
        [dispatchPromise, reduxDispatch, closeAuthModal, setLoginStatus, audmeData, loginChannel],
    );

    const handleSignup = useCallback(
        async (data: SignUpdata, code?: string[], callback?: () => void) => {
            const payload = {
                ...data,
                code: code?.join(''),
            };

            setIsOtpLoading(true);
            try {
                const result = await dispatchPromise(createUserProfile(payload));
                setIsOtpLoading(false);
                callback && callback();

                // Update auth state with new JWT details
                const { isSignedIn, token, tokenExists, user_id } = getJWtDetails();
                setLoginStatus(token || '', isSignedIn, tokenExists, user_id);

                return result;
            } catch (error) {
                setIsOtpLoading(false);
                SentryCapture(error);
            }
        },
        [dispatchPromise, setLoginStatus],
    );

    const handleGetOTP = useCallback(
        async (data: SignUpdata, callback: () => void, resend?: boolean) => {
            const payload = {
                ...data,
                resendToken: resend,
                verificationChannel: data.verificationChannel,
            };

            if (resend) {
                setIsResendingOtp(true);
                setIsOtpLoading(true);
                try {
                    const result = await dispatchPromise(getPhoneVerificationCode(payload));
                    setIsResendingOtp(false);
                    setIsOtpLoading(false);
                    return result;
                } catch (error) {
                    setIsResendingOtp(false);
                    setIsOtpLoading(false);
                    SentryCapture(error);
                }
            } else {
                setIsAuthLoading(true);
                setIsOtpLoading(true);
                try {
                    const result = await dispatchPromise(getPhoneVerificationCode(payload));
                    setIsAuthLoading(false);
                    setIsOtpLoading(false);
                    callback();
                    nextStep();
                    return result;
                } catch (error) {
                    setIsAuthLoading(false);
                    setIsOtpLoading(false);
                    SentryCapture(error);
                }
            }
        },
        [dispatchPromise, nextStep],
    );

    const handleResendLoginOtp = useCallback(
        async (data: TUsernamePassword) => {
            setIsResendingOtp(true);
            try {
                const result = await dispatchPromise(get2FATokenForLogin({ ...data, resendToken: true }));
                setIsResendingOtp(false);
                return result;
            } catch (error) {
                setIsResendingOtp(false);
                SentryCapture(error);
            }
        },
        [dispatchPromise],
    );

    const handleCheckUserAvailability = (data: SignUpdata, next: () => void, field: TUserField) => {
        const payload: IFieldAvailabilityData =
            field === 'email'
                ? { field: 'email', request: { email: data.email, twofa_verified: true } }
                : field === 'phoneNumber'
                ? { field: 'phoneNumber', request: { phoneNumber: data.phoneNumber, twofa_verified: true } }
                : { field: 'userName', request: { userName: data.userName } };
        if (field === 'phoneNumber' && usernameTaken) return;
        setIsAuthLoading(true);
        dispatchPromise(startUserDetailsAvailabilitySearch(payload))
            .then(() => {
                setIsAuthLoading(false);
                if (field !== 'userName') next();
            })
            .catch(e => {
                setIsAuthLoading(false);
            });
    };

    const handleLoadingState = () => setIsAuthLoading(false);

    const handleUsernameCheck = (userName: string) => {
        if (userName.replaceAll(' ', '') !== '') {
            const data = { userName: userName } as SignUpdata;
            handleCheckUserAvailability(data, handleLoadingState, 'userName');
        }
    };

    const handleUserUpdate = async (payload: TOnChangePayload) => {
        await dispatchPromise(updateUserProfile(payload));
        closeAuthModal();
    };

    useEffect(() => {
        if (showAuthModal) openAuthModal(modalState.mode);
    }, [showAuthModal, openAuthModal, modalState.mode]);

    // Combine all context values
    const contextValue: AuthContextType = {
        ...baseAuth,
        setLoginStatus,
        modalState,
        openAuthModal,
        closeAuthModal,
        handleCheckUserAvailability,
        nextStep,
        prevStep,
        handleLogin,
        handleSignup,
        handleGetOTP,
        audmeData,
        handleResendLoginOtp,
        isAuthLoading,
        isOtpLoading,
        isResendingOtp,
        loginMessage,
        loginChannel,
        handleUsernameCheck,
        updateUserDetails: handleUserUpdate,
    };

    return (
        <AuthContext.Provider value={contextValue}>
            <ReCAPTCHA hidden sitekey={process.env.REACT_APP_CAPTCHA_KEY as string} size="invisible" ref={ref as any} />
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook for accessing the auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Convenience hook specifically for authentication modal functionality
export const useAuthModal = () => {
    const {
        isSignedIn,
        openAuthModal,
        closeAuthModal,
        modalState,
        handleLogin,
        handleSignup,
        handleGetOTP,
        handleResendLoginOtp,
        isAuthLoading,
        isOtpLoading,
        isResendingOtp,
    } = useAuth();

    // Helper for wrapping functions that require authentication
    const withAuth = useCallback(
        <T extends (...args: any[]) => any>(fn: T, mode: 'login' | 'signup' = 'login') => {
            return (...args: Parameters<T>): ReturnType<T> | void => {
                if (isSignedIn) {
                    return fn(...args);
                } else {
                    openAuthModal(mode);
                    return undefined as unknown as ReturnType<T>;
                }
            };
        },
        [isSignedIn, openAuthModal],
    );

    return {
        isAuthenticated: isSignedIn,
        openModal: openAuthModal,
        closeModal: closeAuthModal,
        modalState,
        withAuth,
        login: handleLogin,
        signup: handleSignup,
        getOTP: handleGetOTP,
        resendLoginOtp: handleResendLoginOtp,
        isAuthLoading,
        isOtpLoading,
        isResendingOtp,
    };
};
